import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, MapPin, Copy, Check } from 'lucide-react';

interface PolygonInfoPopupProps {
    polygon: any;
    onClose: () => void;
}

const PolygonInfoPopup: React.FC<PolygonInfoPopupProps> = ({ polygon, onClose }) => {
    const popupRef = useRef<HTMLDivElement>(null);
    const [copied, setCopied] = useState(false);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    const handleCopyCoordinates = () => {
        if (polygon.polygon?.geometry?.coordinates) {
            const textToCopy = JSON.stringify(polygon.polygon.geometry.coordinates, null, 2);
            navigator.clipboard.writeText(textToCopy);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    if (!polygon) return null;

    const portalRoot = document.getElementById('map-portal-root') || document.body;

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div
                ref={popupRef}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 w-[300px] overflow-hidden animate-in fade-in zoom-in duration-200"
            >
                <div className="flex items-center justify-between p-2.5 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                    <div className="flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5 text-blue-500" />
                        <h3 className="font-semibold text-xs text-gray-900 dark:text-white">
                            Detalle del Polígono
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>

                <div className="p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 block mb-0.5">ID</span>
                            <code className="text-[10px] bg-gray-100 dark:bg-gray-900 px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-600 block w-full truncate text-gray-700 dark:text-gray-300" title={polygon.id}>
                                {polygon.id || 'Sin ID'}
                            </code>
                        </div>

                        <div>
                            <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 block mb-0.5">Estado</span>
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium
                    ${(polygon.status === true || polygon.status === 'ACTIVE' || polygon.status === 'ACTIVO')
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`}>
                                {(polygon.status === true || polygon.status === 'ACTIVE' || polygon.status === 'ACTIVO') ? 'ACTIVO' : 'INACTIVO'}
                            </span>
                        </div>
                    </div>

                    <div>
                        <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 block mb-0.5">Tipo</span>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium
                ${(polygon.type === 'include' || polygon.type === 'inclusion')
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'}`}>
                            {(polygon.type === 'include' || polygon.type === 'inclusion') ? 'INCLUSIÓN' : 'EXCLUSIÓN'}
                        </span>
                    </div>

                    <div>
                        <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 block mb-0.5">Coberturas Asignadas</span>
                        <div className="flex flex-wrap gap-1">
                            {polygon.coverageIds && polygon.coverageIds.length > 0 ? (
                                polygon.coverageIds.map((id: string, idx: number) => (
                                    <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded-[3px] text-[9px] font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                                        {id}
                                    </span>
                                ))
                            ) : (
                                <span className="text-[10px] text-gray-400 italic">Sin coberturas asignadas</span>
                            )}
                        </div>
                    </div>

                    <div>
                        <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 block mb-0.5">Couriers</span>
                        <div className="flex flex-wrap gap-1">
                            {polygon.courierIds && polygon.courierIds.length > 0 ? (
                                polygon.courierIds.map((id: string, idx: number) => (
                                    <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded-[3px] text-[9px] font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-100 dark:border-blue-800">
                                        {id}
                                    </span>
                                ))
                            ) : (
                                <span className="text-[10px] text-gray-400 italic">Sin couriers asignados</span>
                            )}
                        </div>
                    </div>

                    <div>
                        <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 block mb-0.5">Horario de Trabajo</span>
                        <div className="space-y-1">
                            {polygon.workingTime && polygon.workingTime.length > 0 ? (
                                polygon.workingTime.map((item: any, idx: number) => {
                                    const DAYS_MAP: Record<number, string> = { 1: 'Lu', 2: 'Ma', 3: 'Mi', 4: 'Ju', 5: 'Vi', 6: 'Sá', 7: 'Do' };
                                    const daysLabels = (item.daysOfWeek || []).sort().map((d: number) => DAYS_MAP[d] || d).join(', ');
                                    return (
                                        <div key={idx} className="text-[9px] text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50 p-1 rounded border border-gray-100 dark:border-gray-800">
                                            <span className="font-semibold text-blue-600 dark:text-blue-400">{daysLabels}:</span> {item.from} - {item.to}
                                        </div>
                                    );
                                })
                            ) : (
                                <span className="text-[10px] text-gray-400 italic">Sin horario definido</span>
                            )}
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400">Coordenadas</span>
                            <button
                                onClick={handleCopyCoordinates}
                                className="text-[10px] flex items-center gap-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                                title="Copiar coordenadas"
                            >
                                {copied ? <Check className="w-2.5 h-2.5" /> : <Copy className="w-2.5 h-2.5" />}
                                {copied ? 'Copiado' : 'Copiar'}
                            </button>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-1.5 max-h-24 overflow-y-auto">
                            <code className="text-[9px] leading-tight text-gray-600 dark:text-gray-400 break-all whitespace-pre-wrap font-mono">
                                {polygon.polygon?.geometry?.coordinates
                                    ? JSON.stringify(polygon.polygon.geometry.coordinates, null, 2)
                                    : 'Sin coordenadas disponibles'}
                            </code>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        portalRoot
    );
};

export default PolygonInfoPopup;
